import React, { useState, useEffect, useCallback } from 'react';
import Popup from 'reactjs-popup';
import maskAmount from '../utils/maskAmount';
import CrmPartyAutocomplete from './CrmPartyAutocomplete';
import crmLabels from '../utils/crmLabels';

const axios = require('axios').default;

// Add/Edit Soft Credits for a single donation. A donation can be split across
// several recipients — contacts or organizations — each with a custom amount;
// the sum can't exceed the donation total (enforced server-side). `basePath`
// is the nested soft_credits endpoint of the donor's donation (fan- or
// org-nested); omit props exclude the donor from the recipient picker.
// Styled to match LogDonationModal (ldm-* system).
export default function SoftCreditsModal({
  team, basePath, csrfToken, onClose, onChanged,
  omitFanIds = [], omitOrganizationIds = [],
}) {
  const { organization: orgLabel } = crmLabels(team);
  const orgSingularLower = orgLabel.singular.toLowerCase();
  const [rows, setRows] = useState([]);
  const [attributed, setAttributed] = useState('');
  const [total, setTotal] = useState('');
  const [remaining, setRemaining] = useState('');
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(null);
  const [saving, setSaving] = useState(false);

  // Add/edit form state. editingId != null means we're editing that row.
  // recipient: { type: 'fan' | 'organization', id, name } | null
  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [editingId, setEditingId] = useState(null);

  const applyData = useCallback((data) => {
    setRows(data.soft_credits || []);
    setAttributed(data.attributed_amount || '');
    setTotal(data.total_donation || '');
    setRemaining(data.remaining_amount || '');
  }, []);

  useEffect(() => {
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    let ignore = false;
    axios
      .get(`${basePath}.json`)
      .then((res) => { if (!ignore) applyData(res.data || {}); })
      .catch(() => { if (!ignore) setErrors(['Failed to load soft credits.']); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [basePath, csrfToken, applyData]);

  const resetForm = () => { setRecipient(null); setAmount(''); setEditingId(null); setErrors(null); };

  const numericAmount = parseFloat(amount);
  const canSave = recipient && !isNaN(numericAmount) && numericAmount > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setErrors(null);
    const payload = {
      soft_credit: {
        fan_id: recipient.type === 'fan' ? recipient.id : null,
        organization_id: recipient.type === 'organization' ? recipient.id : null,
        amount: numericAmount,
      },
    };
    try {
      const res = editingId
        ? await axios.put(`${basePath}/${editingId}.json`, payload)
        : await axios.post(`${basePath}.json`, payload);
      applyData(res.data || {});
      resetForm();
      if (onChanged) onChanged();
    } catch (e) {
      setErrors(e.response?.data?.errors || ['Failed to save soft credit.']);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setRecipient(row.recipient);
    setAmount(String(row.amount_value != null ? row.amount_value : ''));
    setErrors(null);
  };

  const handleDelete = async (row) => {
    setErrors(null);
    try {
      const res = await axios.delete(`${basePath}/${row.id}.json`);
      applyData(res.data || {});
      if (editingId === row.id) resetForm();
      if (onChanged) onChanged();
    } catch (e) {
      setErrors(e.response?.data?.errors || ['Failed to remove soft credit.']);
    }
  };

  const summary = [
    { label: 'Attributed', value: attributed || '$0.00' },
    { label: 'Total', value: total || '$0.00' },
    { label: 'Remaining', value: remaining || '$0.00' },
  ];

  return (
    <Popup
      modal
      open
      onClose={onClose}
      contentStyle={{
        border: 'none',
        borderRadius: '16px',
        boxShadow: '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03)',
        padding: '0px',
        width: '480px',
        maxWidth: '90vw',
        background: 'none',
      }}
      overlayStyle={{ zIndex: '1050', backgroundColor: 'rgba(10, 13, 18, 0.5)' }}
      closeOnDocumentClick
      closeOnEscape
    >
      <div className="ldm-modal" role="dialog" aria-modal="true" aria-labelledby="scm-title" data-testid="soft-credits-modal">
        <div className="ldm-header">
          <div className="ldm-icon" aria-hidden="true">
            <i className="fas fa-hand-holding-heart" />
          </div>
          <button type="button" className="ldm-close" onClick={onClose} aria-label="Close">
            <i className="fa fa-times" />
          </button>
          <h5 className="ldm-title" id="scm-title">Soft Credits</h5>
          <p className="ldm-subtitle">{`Credit another contact or ${orgSingularLower} for influencing this donation.`}</p>
        </div>

        <div className="ldm-field">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="ldm-label">Recipient</label>
              <CrmPartyAutocomplete
                team={team}
                omitFanIds={omitFanIds}
                omitOrganizationIds={omitOrganizationIds}
                value={recipient}
                onChange={setRecipient}
                placeholder={`Search contacts or ${orgLabel.plural.toLowerCase()}…`}
                testId="soft-credit-recipient"
              />
            </div>
            <div style={{ width: '110px' }}>
              <label className="ldm-label">Amount</label>
              <input
                type="text"
                inputMode="decimal"
                className="ldm-input"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(maskAmount(e.target.value))}
                data-testid="soft-credit-amount"
              />
            </div>
            <button
              type="button"
              className="btn-setlist-primary"
              style={{ whiteSpace: 'nowrap' }}
              disabled={!canSave}
              onClick={handleSave}
              data-testid="soft-credit-save"
            >
              {saving ? 'Saving…' : editingId ? 'Update' : 'Add'}
            </button>
          </div>
          {editingId && (
            <button type="button" className="btn btn-link btn-sm p-0 mt-1 text-muted" onClick={resetForm}>
              Cancel edit
            </button>
          )}
        </div>

        {errors && (
          <div className="ldm-error" role="alert" data-testid="soft-credit-errors">
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: '12px',
            padding: '12px 16px',
            background: '#f9fafb',
            borderRadius: '10px',
            marginBottom: '16px',
          }}
        >
          {summary.map((s) => (
            <div key={s.label} style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#333' }} data-testid={`soft-credit-${s.label.toLowerCase()}`}>
                {s.value}
              </div>
              <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#717680' }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <div className="ldm-section"><span className="ldm-section-label">Soft credit history</span></div>
        {loading ? (
          <p className="ldm-subtitle">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="ldm-subtitle">No soft credits yet.</p>
        ) : (
          rows.map((row) => (
            <div
              key={row.id}
              data-testid={`soft-credit-row-${row.id}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}
            >
              <div>
                <div style={{ fontWeight: 600, color: '#333' }}>
                  {row.recipient?.type === 'organization' && (
                    <i className="fal fa-building text-muted me-1" aria-hidden="true" />
                  )}
                  {row.recipient?.name}
                </div>
                <div className="ldm-subtitle">{row.amount}</div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                <button type="button" className="btn btn-link btn-sm p-0 me-3 text-muted" aria-label="Edit" onClick={() => handleEdit(row)}>
                  <i className="fa fa-pencil" />
                </button>
                <button type="button" className="btn btn-link btn-sm p-0 text-danger" aria-label="Delete" onClick={() => handleDelete(row)}>
                  <i className="fa fa-trash" />
                </button>
              </div>
            </div>
          ))
        )}

        <div className="ldm-footer">
          <button type="button" className="btn-setlist-secondary" onClick={onClose}>Done</button>
        </div>
      </div>
    </Popup>
  );
}
